/**
 * `JournalSegment.publicText` backport -- the only place that touches the journal DB rows.
 *
 * The parsing (`DiscordExport.ts`) and the matching (`Match.ts`) are pure; this thin wrapper does the
 * two things that need the app: read the user's journal entries out of the store, and write one
 * segment's `publicText` back through `UpdateJournalEntry`.
 *
 * The write path mirrors `UI/Journal/JournalEntry/SegmentUI/TextTabs/@Shared.ts` (quick-backup +
 * deep clone + `UpdateJournalEntry`), because a segment that was accidentally overwritten is
 * otherwise unrecoverable -- and this tool writes text the user has not seen in the DB UI yet.
 */
import {Clone_KeepTypeDef} from "../Utils/General/General.js";
import {UpdateJournalEntry} from "../Server/Commands/JournalEntryCommands.js";
import {JournalEntry, JournalSegment} from "../Server/DB/journalEntries/@JournalEntry.js";
import {GetJournalEntries} from "../Server/DB/journalEntries.js";
import {MeID} from "../Server/DB/users.js";

/** Every journal entry of the signed-in user (empty when signed out -- the tool needs a signed-in
 * session anyway, since it writes through the command system). */
export function GetJournalImportEntries(): JournalEntry[] {
	return GetJournalEntries(MeID(), "desc");
}

/** Last-line-of-defence backups of everything this tool overwrote (see the matching note in
 * `TextTabs/@Shared.ts`); the value is what the segment held BEFORE the apply. */
export const journalImportSegmentBackups = [] as Array<{entryId: string, segmentId: string|n, publicText: string, at: number}>;

/** Writes `publicText` onto one segment. Returns the previous value (for the "undo last apply" path). */
export async function ApplyPublicTextToSegment(entry: JournalEntry, segmentIndex: number, publicText: string) {
	const segment = entry.segments[segmentIndex];
	if (segment == null) throw new Error("Segment not found (the entry may have changed since the draft was matched).");

	journalImportSegmentBackups.push({entryId: entry.id, segmentId: segment.id, publicText: segment.publicText ?? "", at: Date.now()});

	const segments_new = Clone_KeepTypeDef(entry.segments);
	segments_new[segmentIndex] = segment.Extended({publicText}) as JournalSegment;
	await new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
	return segment.publicText ?? "";
}
